/**
 * WhatsAppInboxView.jsx — painel de CONVERSAS DO WHATSAPP da casca (abre pelo sino ao lado do chip de créditos).
 * Pura: recebe { vm, actions }. Estiliza só via `sx` + CSS vars. Contrato: shell/whatsappInbox.vm.js (@version 2).
 *
 * É CONVERSA, não notificação. O desenho carrega as três regras do produto:
 *   1. Só o que o cliente mandou "grita": linha de conversa com recebida não lida ganha fundo verde, trilho, nome em
 *      negrito, horário em verde e a pastilha com o número; dentro da conversa, as recebidas novas vêm depois do
 *      divisor "Novas mensagens" e com a etiqueta "Nova". O que nós enviamos é contexto: tinta calma, sem número.
 *   2. O rótulo importa tanto quanto o texto: toda bolha abre com o `label` (+ ícone pelo `kind`). "Tocou no botão"
 *      tem cara de TECLA apertada (não de texto), citando a mensagem em que o cliente tocou.
 *   3. @v3 — o dono RESPONDE aqui: o rodapé cinza é a caixa de escrever do WhatsApp (pílula branca + botão verde
 *      redondo). Enter envia, Shift+Enter quebra linha. Fora do prazo de 24 h, sem crédito ou se o cliente nunca
 *      escreveu, a caixa vira um CARTÃO DE SISTEMA (regra do WhatsApp, não erro do dono) com a saída. O (?) abre as
 *      regras de envio (popover no desktop, folha no celular). "Abrir no meu WhatsApp" desceu para um ícone no
 *      cabeçalho: agora que dá para responder aqui, ele é a saída secundária, não o caminho principal.
 *
 * Lista = painel do Admin (segue tema e modo). Conversa aberta = cromo do WhatsApp, o mesmo da WhatsPreview de
 * Integrações (fixo, não escurece com o painel) — ver o bloco CONVERSA.
 * Estado UI-local (permitido): o scroll da conversa e as regras de envio abertas/fechadas. O texto digitado é do
 * container (composer.text) — a View só repassa cada tecla (onComposerChange).
 */
import { useMemo, useRef, useLayoutEffect, useState } from 'react';
import {
  Dialog, Box, Typography, IconButton, Button, TextField, InputAdornment, Skeleton, Tooltip,
  CircularProgress, InputBase, Popover, Drawer, useMediaQuery, useTheme,
} from '@mui/material';
import SendRounded from '@mui/icons-material/SendRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CheckRounded from '@mui/icons-material/CheckRounded';
import WhatsApp from '@mui/icons-material/WhatsApp';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ClearRounded from '@mui/icons-material/ClearRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
import DoneRounded from '@mui/icons-material/DoneRounded';
import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import MoneyOffRounded from '@mui/icons-material/MoneyOffRounded';
import TouchAppRounded from '@mui/icons-material/TouchAppRounded';
import ReplyRounded from '@mui/icons-material/ReplyRounded';
import MicRounded from '@mui/icons-material/MicRounded';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import EmojiEmotionsOutlined from '@mui/icons-material/EmojiEmotionsOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ContactPageOutlined from '@mui/icons-material/ContactPageOutlined';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import ChatOutlined from '@mui/icons-material/ChatOutlined';
import EventRounded from '@mui/icons-material/EventRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

/* ── Cromo do WhatsApp — o painel INTEIRO (lista e conversa) ─────────────────────────────
 * O dono abre o painel e está "dentro do WhatsApp" do começo ao fim: a lista é a tela de conversas do
 * app (cabeçalho petróleo, busca em pílula cinza, linhas brancas com divisória recuada, hora e contador
 * verdes na não lida) e a conversa é o MESMO desenho da WhatsPreview de Integrações: papel bege (--wa-chat-bg), bolha
 * verde (--wa-bubble), bolha do cliente branca, cabeçalho petróleo (--wa-header), chip de dia azulado em
 * caixa alta, tiques azuis, "rabinho" na primeira bolha de cada sequência e a barra cinza no rodapé no
 * lugar do campo de digitação (como o WhatsApp faz quando não se pode escrever). Como na prévia, é cromo
 * de MARCA: não segue tema nem modo — o WhatsApp do cliente não escurece com o painel.
 * ⚠️ VARS NOVAS a registrar no schema (fixas, família --wa-*). Todas lidas COM fallback, então já
 *    funcionam hoje: --wa-bubble-in, --wa-on-header, --wa-text, --wa-meta, --wa-tick-read, --wa-day-bg,
 *    --wa-day-fg, --wa-quote-bg, --wa-footer-bg, --wa-avatar-bg, --wa-danger, --wa-warning, --wa-bubble-shadow,
 *    --wa-divider, --wa-row-hover.
 * Contraste (fallbacks): texto #111B21 ≥ 15:1 nas duas bolhas; hora #56656F 5,1:1 na verde e 5,7:1 na
 * branca; chip de dia #54656F/#D4EAF7 4,9:1; rótulo verde-petróleo (--wa-header) 6,9:1 na verde. */
const WAC = {
  chatBg: 'var(--wa-chat-bg)',
  bubbleOut: 'var(--wa-bubble)',
  bubbleIn: 'var(--wa-bubble-in, #FFFFFF)',
  header: 'var(--wa-header)',
  onHeader: 'var(--wa-on-header, #FFFFFF)',
  onHeaderSub: 'color-mix(in srgb, var(--wa-on-header, #FFFFFF) 76%, var(--wa-header))',
  onHeaderSoft: 'color-mix(in srgb, var(--wa-on-header, #FFFFFF) 14%, var(--wa-header))',
  onHeaderLine: 'color-mix(in srgb, var(--wa-on-header, #FFFFFF) 38%, var(--wa-header))',
  text: 'var(--wa-text, #111B21)',
  meta: 'var(--wa-meta, #56656F)',
  tickRead: 'var(--wa-tick-read, #53BDEB)',
  dayBg: 'var(--wa-day-bg, #D4EAF7)',
  dayFg: 'var(--wa-day-fg, #54656F)',
  quoteBg: 'var(--wa-quote-bg, rgba(11, 20, 26, 0.06))',
  footerBg: 'var(--wa-footer-bg, #F0F2F5)',
  avatarBg: 'var(--wa-avatar-bg, #DFE5E7)',
  danger: 'var(--wa-danger, #B42318)',
  warning: 'var(--wa-warning, #7A4A00)',
  shadow: 'var(--wa-bubble-shadow, 0 1px .5px rgba(11, 20, 26, 0.13))',
  ink: 'var(--wa-header)',
  divider: 'var(--wa-divider, #E9EDEF)',
  rowHover: 'var(--wa-row-hover, #F5F6F6)',
};
const STATUS_INK = { error: WAC.danger, warning: WAC.warning, success: WAC.ink, neutral: WAC.meta };
const WA_TICKS = {
  sending: { Icon: AccessTimeRounded, color: WAC.meta },   // @v3 o relógio do WhatsApp: gravada, ainda não saiu
  sent: { Icon: DoneRounded, color: WAC.meta },
  delivered: { Icon: DoneAllRounded, color: WAC.meta },
  read: { Icon: DoneAllRounded, color: WAC.tickRead },
};

function OnHeaderIconButton({ label, onClick, children }) {
  return (
    <Tooltip title={label}>
      <IconButton aria-label={label} onClick={onClick}
        sx={{
          ...TAP, flexShrink: 0, color: WAC.onHeader,
          '&:hover': { bgcolor: WAC.onHeaderSoft },
          '&:focus-visible': { outline: '2px solid', outlineColor: WAC.onHeader, outlineOffset: '-2px' },
        }}>
        {children}
      </IconButton>
    </Tooltip>
  );
}

const FOCUS = { '&:focus-visible': { outline: '2px solid', outlineColor: 'var(--wa-header)', outlineOffset: '2px' } };
const TAP = { width: { xs: 44, sm: 40 }, height: { xs: 44, sm: 40 } };

// Ícone pelo TIPO técnico da mensagem (kind) — o texto do rótulo vem pronto do VM e nunca é interpretado aqui.
const KIND_ICON = {
  template: NotificationsActiveOutlined,
  reply: SmartToyOutlined,
  business: EditOutlined,   // @v3 escrita pelo dono — enviada comum, sem o selo de automática
  button: TouchAppRounded,
  text: ChatOutlined,
  audio: MicRounded,
  image: ImageOutlined,
  sticker: EmojiEmotionsOutlined,
  video: VideocamOutlined,
  document: DescriptionOutlined,
  contact: ContactPageOutlined,
  media: AttachFileRounded,
};
const kindIcon = (k) => KIND_ICON[k] || ChatOutlined;

// Tiques (delivery.state). `read` e `delivered` têm o MESMO desenho no WhatsApp (✓✓) e só a cor muda —
// por isso o rótulo ("Lida"/"Entregue") vai sempre escrito ao lado: estado nunca só por cor.
const TICKS = {
  sending: { Icon: AccessTimeRounded, color: WAC.meta },
  sent: { Icon: DoneRounded, color: WAC.meta },
  delivered: { Icon: DoneAllRounded, color: WAC.meta },
  read: { Icon: DoneAllRounded, color: WAC.tickRead },
  failed: { Icon: ErrorOutlineRounded, color: WAC.danger },
  not_sent: { Icon: MoneyOffRounded, color: WAC.warning },
};

/* ── Negrito do WhatsApp (*Corte*) — o texto chega como o cliente recebeu ──────────── */
function WaText({ text }) {
  if (text == null) return null;
  return String(text).split(/(\*[^*\n]+\*)/g).map((part, i) => (
    /^\*[^*\n]+\*$/.test(part)
      ? <Box key={i} component="strong" sx={{ fontWeight: 'var(--fw-bold)' }}>{part.slice(1, -1)}</Box>
      : part
  ));
}

/* ── Avatar do WhatsApp: disco cinza; iniciais em petróleo, ou a silhueta quando não há nome ── */
function WaAvatar({ initials, size = 48 }) {
  return (
    <Box aria-hidden sx={{
      width: size, height: size, flexShrink: 0, borderRadius: '50%', bgcolor: WAC.avatarBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: initials ? WAC.header : WAC.bubbleIn,
    }}>
      {initials
        ? <Typography component="span" variant="subtitle2" sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit', letterSpacing: '.02em' }}>{initials}</Typography>
        : <PersonRounded sx={{ fontSize: size * 0.62 }} />}
    </Box>
  );
}

/* ══════════════════════════════════ LISTA ══════════════════════════════════ */

function ListHeader({ onClose }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pl: 2, pr: 0.5, py: 1, minHeight: 60, bgcolor: WAC.header, color: WAC.onHeader, flexShrink: 0 }}>
      <WhatsApp aria-hidden sx={{ fontSize: 26 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography id="wa-inbox-title" component="h2" variant="subtitle1" noWrap sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>
          Conversas
        </Typography>
        <Typography variant="caption" noWrap sx={{ color: WAC.onHeaderSub }}>
          O que seus clientes disseram ao Agendário
        </Typography>
      </Box>
      <OnHeaderIconButton label="Fechar conversas" onClick={onClose}><CloseRounded /></OnHeaderIconButton>
    </Box>
  );
}

/* busca em pílula cinza, como no WhatsApp — sem contorno, foco com fio petróleo */
function SearchField({ value, onSearch }) {
  return (
    <Box sx={{ px: 1.5, py: 1, bgcolor: WAC.bubbleIn, borderBottom: '1px solid', borderColor: WAC.divider, flexShrink: 0 }}>
      <TextField
        fullWidth size="small" value={value || ''}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Pesquisar nome ou telefone"
        inputProps={{ 'aria-label': 'Pesquisar conversa por nome ou telefone' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><SearchRounded sx={{ fontSize: 20, color: WAC.meta }} /></InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Limpar busca" onClick={() => onSearch('')}
                sx={{ width: 32, height: 32, color: WAC.meta, ...FOCUS }}>
                <ClearRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px', bgcolor: WAC.footerBg, minHeight: 40, color: WAC.text,
            '& fieldset': { border: 0 },
            '&.Mui-focused fieldset': { border: '2px solid', borderColor: WAC.ink },
          },
          '& input::placeholder': { color: WAC.meta, opacity: 1 },
        }}
      />
    </Box>
  );
}

/* prévia da última mensagem na linha da conversa */
function LastMessagePreview({ m, hot }) {
  if (!m) return null;
  const out = m.direction === 'out';
  const KIcon = kindIcon(m.kind);
  const tick = out && m.delivery ? TICKS[m.delivery.state] : null;
  const showLabel = m.kind !== 'text' || !m.text;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1, color: hot ? WAC.text : WAC.meta }}>
      {tick && <tick.Icon aria-hidden sx={{ fontSize: 17, flexShrink: 0, color: tick.color }} />}
      {m.kind !== 'text' && <KIcon aria-hidden sx={{ fontSize: 16, flexShrink: 0, color: WAC.meta }} />}
      <Typography variant="body2" noWrap sx={{ minWidth: 0, color: 'inherit', fontWeight: hot ? 'var(--fw-medium)' : 'var(--fw-regular)' }}>
        {showLabel && (
          <Box component="span" sx={{ fontWeight: hot ? 'var(--fw-bold)' : 'var(--fw-medium)' }}>
            {m.label}{m.text ? ': ' : ''}
          </Box>
        )}
        {m.text && <WaText text={m.text} />}
      </Typography>
    </Box>
  );
}

function UnreadPill({ label }) {
  if (!label) return null;
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      minWidth: 22, height: 22, px: 0.75, borderRadius: 999, bgcolor: 'var(--wa-main)', color: 'var(--wa-on-main)',
    }}>
      <Typography component="span" variant="caption" sx={{ fontWeight: 'var(--fw-bold)', fontVariantNumeric: 'tabular-nums', color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );
}

/* linha da conversa como no WhatsApp: avatar, nome + hora, prévia + contador; divisória recuada.
   Não lida = nome e prévia em negrito/tinta cheia, hora em verde-petróleo negrito e o contador verde
   (nunca só cor: negrito + número). Só nossas = tudo em cinza, calmo. */
function ConversationRow({ c, onOpen }) {
  const hot = (c.unreadCount || 0) > 0;
  const aria = `${c.title}${hot ? `, ${c.unreadLabel} ${c.unreadCount === 1 ? 'mensagem não lida' : 'não lidas'}` : ''}, ${c.lastTimeLabel}`;
  return (
    <Box component="li" sx={{ listStyle: 'none' }}>
      <Box component="button" type="button" onClick={onOpen} aria-label={aria}
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, pl: 1.5, pr: 0, py: 0,
          minHeight: 72, border: 0, m: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer',
          bgcolor: WAC.bubbleIn, color: WAC.text, transition: 'background-color .12s',
          '&:hover': { bgcolor: WAC.rowHover },
          '&:focus-visible': { outline: 'none', boxShadow: 'inset 0 0 0 2px var(--wa-header)' },
        }}>
        <WaAvatar initials={c.initials} />
        <Box sx={{
          flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.375,
          pr: 1.75, borderBottom: '1px solid', borderColor: WAC.divider,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
            <Typography variant="body1" noWrap sx={{ flex: 1, minWidth: 0, color: WAC.text, fontWeight: hot ? 'var(--fw-bold)' : 'var(--fw-regular)' }}>
              {c.title}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0, whiteSpace: 'nowrap', color: hot ? WAC.ink : WAC.meta, fontWeight: hot ? 'var(--fw-bold)' : 'var(--fw-regular)' }}>
              {c.lastTimeLabel}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <LastMessagePreview m={c.lastMessage} hot={hot} />
            <UnreadPill label={c.unreadLabel} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function RowSkeleton({ i = 0 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1.5, minHeight: 72, bgcolor: WAC.bubbleIn }}>
      <Skeleton variant="circular" width={48} height={48} sx={{ bgcolor: WAC.divider, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'center', pr: 1.75, borderBottom: '1px solid', borderColor: WAC.divider }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Skeleton variant="text" width={i % 2 ? '44%' : '58%'} sx={{ bgcolor: WAC.divider }} />
          <Skeleton variant="text" width={40} sx={{ bgcolor: WAC.divider }} />
        </Box>
        <Skeleton variant="text" width={i % 3 === 1 ? '64%' : '86%'} sx={{ bgcolor: WAC.divider }} />
      </Box>
    </Box>
  );
}

/* rodapé na barra cinza do WhatsApp — a mesma da conversa (onde lá vai a nota de só leitura) */
function ListFooter({ onSeeAll }) {
  return (
    <Box sx={{ bgcolor: WAC.footerBg, px: 1, py: 0.75, display: 'flex', justifyContent: 'center', flexShrink: 0, pb: { xs: 'max(6px, env(safe-area-inset-bottom))', sm: 0.75 } }}>
      <Button onClick={onSeeAll} startIcon={<HistoryRounded />}
        sx={{ minHeight: 40, px: 1.5, color: WAC.ink, fontWeight: 'var(--fw-bold)', borderRadius: 999, '&:hover': { bgcolor: WAC.divider }, ...FOCUS }}>
        Ver histórico completo
      </Button>
    </Box>
  );
}

function ConversationList({ vm, a }) {
  const list = vm.conversations || [];
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: WAC.bubbleIn }}>
      <Box component="ul" aria-label="Conversas" sx={{ m: 0, p: 0 }}>
        {list.map((c) => <ConversationRow key={c.key} c={c} onOpen={() => a.onOpenConversation(c.key)} />)}
      </Box>
      {vm.loadingMoreConversations && (
        <Box role="status" aria-label="Carregando conversas anteriores">
          <RowSkeleton i={1} /><RowSkeleton i={2} />
        </Box>
      )}
      {vm.hasMoreConversations && !vm.loadingMoreConversations && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <Button onClick={() => a.onLoadMoreConversations()} startIcon={<ExpandMoreRounded />}
            sx={{
              minHeight: 40, px: 2, borderRadius: 999, color: WAC.ink, fontWeight: 'var(--fw-bold)',
              bgcolor: WAC.footerBg, '&:hover': { bgcolor: WAC.divider }, ...FOCUS,
            }}>
            Carregar conversas anteriores
          </Button>
        </Box>
      )}
    </Box>
  );
}

/* blocos de estado (vazio, sem resultado, erro) — na folha branca do WhatsApp */
function StateBlock({ icon, iconBg = WAC.footerBg, title, description, action, children }) {
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: WAC.bubbleIn, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 3, py: 4 }}>
      {children}
      {icon && (
        <Box aria-hidden sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
      )}
      <Typography variant="subtitle1" component="p" sx={{ fontWeight: 'var(--fw-bold)', color: WAC.text, mb: 0.5, textWrap: 'balance' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: WAC.meta, maxWidth: 320, textWrap: 'pretty' }}>{description}</Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/* vazio com personalidade: uma conversa-fantasma mostra o que vai aparecer aqui */
function EmptyGhostThread() {
  const ghost = (side, w, bg) => (
    <Box sx={{ alignSelf: side, width: w, height: 22, borderRadius: 'var(--radius-sm)', bgcolor: bg, boxShadow: WAC.shadow }} />
  );
  return (
    <Box aria-hidden sx={{
      width: '100%', maxWidth: 260, mb: 3, p: 1.75, borderRadius: 'var(--radius-card)', bgcolor: WAC.chatBg,
      display: 'flex', flexDirection: 'column', gap: 0.75, border: '1px solid', borderColor: WAC.divider,
    }}>
      {ghost('flex-end', '72%', WAC.bubbleOut)}
      <Box sx={{ alignSelf: 'flex-end', display: 'flex', gap: '2px', width: '72%' }}>
        <Box sx={{ flex: 1, height: 18, borderRadius: 'var(--radius-sm)', bgcolor: WAC.bubbleOut, opacity: 0.7 }} />
        <Box sx={{ flex: 1, height: 18, borderRadius: 'var(--radius-sm)', bgcolor: WAC.bubbleOut, opacity: 0.7 }} />
      </Box>
      <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: WAC.bubbleIn, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: WAC.shadow }}>
          <TouchAppRounded sx={{ fontSize: 14, color: WAC.meta }} />
        </Box>
        {ghost('flex-start', 88, WAC.bubbleIn)}
      </Box>
      {ghost('flex-start', '56%', WAC.bubbleIn)}
    </Box>
  );
}

/* ══════════════════════════════════ CONVERSA ══════════════════════════════════ */

/* WhatsApp + seta de link externo: "isto sai do painel e abre o SEU WhatsApp" */
function OpenInWhatsAppGlyph() {
  return (
    <Box aria-hidden sx={{ position: 'relative', display: 'flex' }}>
      <WhatsApp sx={{ fontSize: 22 }} />
      <NorthEastRounded sx={{ position: 'absolute', right: -6, top: -6, fontSize: 13 }} />
    </Box>
  );
}

function ConversationHeader({ conv, a }) {
  const sub = conv.phoneLabel && conv.phoneLabel !== conv.title ? conv.phoneLabel : null;
  return (
    <Box sx={{ bgcolor: WAC.header, color: WAC.onHeader, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.5, py: 0.75 }}>
        <OnHeaderIconButton label="Voltar para as conversas" onClick={() => a.onBackToList()}><ArrowBackRounded /></OnHeaderIconButton>
        {/* com cadastro, o bloco do contato abre a ficha — como tocar no topo da conversa no WhatsApp */}
        <Box
          component={conv.personHref ? 'button' : 'div'}
          type={conv.personHref ? 'button' : undefined}
          onClick={conv.personHref ? () => a.onOpenPerson(conv.personHref) : undefined}
          aria-label={conv.personHref ? `Abrir cadastro de ${conv.title}` : undefined}
          sx={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 44,
            px: 0.5, border: 0, m: 0, font: 'inherit', textAlign: 'left', bgcolor: 'transparent', color: 'inherit',
            borderRadius: 'var(--radius-sm)', cursor: conv.personHref ? 'pointer' : 'default',
            ...(conv.personHref ? {
              '&:hover': { bgcolor: WAC.onHeaderSoft },
              '&:focus-visible': { outline: '2px solid', outlineColor: WAC.onHeader, outlineOffset: '-2px' },
            } : {}),
          }}>
          <Box aria-hidden sx={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0, bgcolor: WAC.avatarBg, color: WAC.header,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {conv.initials
              ? <Typography component="span" variant="subtitle2" sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{conv.initials}</Typography>
              : <PersonRounded sx={{ fontSize: 24 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography id="wa-inbox-title" component="h2" variant="subtitle2" noWrap sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>
              {conv.title}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: WAC.onHeaderSub, fontVariantNumeric: 'tabular-nums' }}>
              {sub}{sub && conv.personHref ? ' · ' : ''}{conv.personHref ? 'ver cadastro' : ''}
            </Typography>
          </Box>
        </Box>
        {conv.replyHref && (
          <OnHeaderIconButton label="Abrir no meu WhatsApp — a resposta sai do seu número" onClick={() => a.onReplyOnWhatsApp(conv.replyHref)}>
            <OpenInWhatsAppGlyph />
          </OnHeaderIconButton>
        )}
        <OnHeaderIconButton label="Fechar conversas" onClick={() => a.onClose()}><CloseRounded /></OnHeaderIconButton>
      </Box>

    </Box>
  );
}

function DayChip({ label }) {
  return (
    <Box component="li" sx={{ listStyle: 'none', display: 'flex', justifyContent: 'center', mt: 1.5, mb: 0.5 }}>
      <Typography variant="caption" sx={{
        px: 1.5, py: '3px', borderRadius: '8px', bgcolor: WAC.dayBg, color: WAC.dayFg, boxShadow: WAC.shadow,
        fontWeight: 'var(--fw-medium)', textTransform: 'uppercase', letterSpacing: '.02em',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

/* a faixa de "não lidas" do WhatsApp: atravessa a conversa de ponta a ponta */
function NewMessagesDivider() {
  return (
    <Box component="li" sx={{
      listStyle: 'none', mx: { xs: -1.25, sm: -1.5 }, mt: 1.5, mb: 0.5, py: 0.625, textAlign: 'center',
      bgcolor: 'color-mix(in srgb, var(--wa-bubble-in, #FFFFFF) 62%, transparent)',
    }}>
      <Typography component="span" variant="caption" sx={{ color: WAC.dayFg, fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: '.03em' }}>
        Novas mensagens
      </Typography>
    </Box>
  );
}

/* o que o WhatsApp de verdade NÃO tem: a mensagem ancorada no agendamento, com o status legível */
function AppointmentChip({ ap }) {
  const c = STATUS_INK[ap.status] || WAC.meta;
  const Icon = ap.status === 'error' ? CancelRounded : ap.status === 'warning' ? HourglassTopRounded : ap.status === 'success' ? CheckCircleRounded : null;
  return (
    <Box sx={{
      mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', rowGap: 0.5,
      px: 1, py: 0.75, borderRadius: '7px', bgcolor: WAC.quoteBg,
    }}>
      <EventRounded aria-hidden sx={{ fontSize: 18, color: WAC.meta, flexShrink: 0 }} />
      <Box sx={{ flex: '1 1 110px', minWidth: 0 }}>
        <Typography variant="caption" noWrap sx={{ color: WAC.text, fontWeight: 'var(--fw-bold)' }}>{ap.label}</Typography>
        <Typography variant="caption" noWrap sx={{ color: WAC.meta }}>{ap.whenLabel}</Typography>
      </Box>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0, px: 0.875, py: '2px', borderRadius: 999,
        color: c, bgcolor: `color-mix(in srgb, ${c} 10%, var(--wa-bubble-in, #FFFFFF))`,
        border: '1px solid', borderColor: `color-mix(in srgb, ${c} 32%, var(--wa-bubble-in, #FFFFFF))`,
      }}>
        {Icon ? <Icon aria-hidden sx={{ fontSize: 14 }} /> : <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c }} />}
        <Typography component="span" variant="caption" sx={{ color: 'inherit', fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap' }}>{ap.statusLabel}</Typography>
      </Box>
    </Box>
  );
}

/* a mensagem citada, como o WhatsApp mostra quando a pessoa responde uma mensagem */
function Quote({ q }) {
  const bar = q.direction === 'out' ? WAC.ink : 'var(--wa-action)';
  return (
    <Box sx={{ mb: 0.75, px: 1, py: 0.625, borderRadius: '7px', bgcolor: WAC.quoteBg, borderLeft: '4px solid', borderLeftColor: bar, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: WAC.ink, fontWeight: 'var(--fw-bold)' }}>{q.label}</Typography>
      {q.text && (
        <Typography variant="caption" sx={{ color: WAC.meta, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          <WaText text={q.text} />
        </Typography>
      )}
    </Box>
  );
}

/* hora + tiques no canto da bolha. "Lida" e "Entregue" têm o mesmo ✓✓ no WhatsApp (só a cor muda),
   então o rótulo vai escrito ao lado — estado nunca só por cor. */
function MetaLine({ m }) {
  const d = m.direction === 'out' ? m.delivery : null;
  const tick = d ? WA_TICKS[d.state] : null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.375, mt: 0.25 }}>
      {tick && <Typography component="span" variant="caption" sx={{ color: WAC.meta }}>{d.label}</Typography>}
      <Typography component="span" variant="caption" sx={{ color: WAC.meta, fontVariantNumeric: 'tabular-nums' }}>{m.timeLabel}</Typography>
      {tick && <tick.Icon aria-hidden sx={{ fontSize: 16, color: tick.color }} />}
    </Box>
  );
}

/* corpo da bolha pelo tipo — sem player: só a cara do que é */
function MessageBody({ m }) {
  const KIcon = kindIcon(m.kind);
  const caption = m.text ? (
    <Typography variant="body2" sx={{ color: WAC.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.75 }}>
      <WaText text={m.text} />
    </Typography>
  ) : null;

  if (m.kind === 'button') {
    // o toque tem cara de TECLA: pílula com fio e "degrau" embaixo, não um texto solto
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.625, mt: 0.25, borderRadius: 999,
        bgcolor: WAC.bubbleIn, color: WAC.ink, border: '1.5px solid',
        borderColor: 'color-mix(in srgb, var(--wa-header) 30%, var(--wa-bubble-in, #FFFFFF))',
        boxShadow: 'inset 0 -2px 0 color-mix(in srgb, var(--wa-header) 22%, var(--wa-bubble-in, #FFFFFF))',
      }}>
        <TouchAppRounded aria-hidden sx={{ fontSize: 18 }} />
        <Typography component="span" variant="body2" sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{m.text || m.label}</Typography>
      </Box>
    );
  }
  if (m.kind === 'audio') {
    // nota de voz do WhatsApp: play, trilho com o ponto e o microfone verde
    return (
      <>
        <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: 190, sm: 220 }, py: 0.25 }}>
          <PlayArrowRounded sx={{ fontSize: 30, color: WAC.meta }} />
          <Box sx={{ flex: 1, position: 'relative', height: 3, borderRadius: 3, bgcolor: 'color-mix(in srgb, var(--wa-meta, #56656F) 35%, transparent)' }}>
            <Box sx={{ position: 'absolute', left: 0, top: '50%', width: 11, height: 11, borderRadius: '50%', transform: 'translateY(-50%)', bgcolor: 'var(--wa-action)' }} />
          </Box>
          <Box sx={{ position: 'relative', width: 36, height: 36, borderRadius: '50%', bgcolor: WAC.avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PersonRounded sx={{ fontSize: 24, color: 'var(--wa-bubble-in, #FFFFFF)' }} />
            <MicRounded sx={{ position: 'absolute', right: -4, bottom: -2, fontSize: 18, color: 'var(--wa-action)' }} />
          </Box>
        </Box>
        {caption}
      </>
    );
  }
  if (m.kind === 'image' || m.kind === 'video') {
    return (
      <>
        <Box aria-hidden sx={{
          width: { xs: 210, sm: 240 }, maxWidth: '100%', aspectRatio: '4 / 3', borderRadius: '7px', bgcolor: WAC.quoteBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: WAC.meta,
        }}>
          {m.kind === 'video' ? <PlayArrowRounded sx={{ fontSize: 44 }} /> : <KIcon sx={{ fontSize: 40 }} />}
        </Box>
        {caption}
      </>
    );
  }
  if (m.kind === 'document' || m.kind === 'contact' || m.kind === 'media') {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderRadius: '7px', bgcolor: WAC.quoteBg, minWidth: 180 }}>
          <KIcon aria-hidden sx={{ fontSize: 26, color: WAC.meta }} />
          <Typography variant="body2" sx={{ fontWeight: 'var(--fw-medium)', color: WAC.text }}>{m.label}</Typography>
        </Box>
        {caption}
      </>
    );
  }
  if (!m.text) {
    return <Typography variant="body2" sx={{ color: WAC.meta, fontStyle: 'italic' }}>Texto não guardado — mensagem antiga</Typography>;
  }
  return (
    <Typography variant="body2" sx={{ color: WAC.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
      <WaText text={m.text} />
    </Typography>
  );
}

/* os botões que o cliente viu embaixo do lembrete — como na prévia de Integrações (caixas brancas) */
function SentButtons({ buttons }) {
  return (
    <Box role="group" aria-label={`Botões que o cliente recebeu: ${buttons.join(', ')}`}
      sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: '4px' }}>
      {buttons.map((b) => (
        <Box key={b} aria-hidden sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1, px: 1.25,
          borderRadius: '9px', bgcolor: WAC.bubbleIn, boxShadow: WAC.shadow, color: WAC.ink,
        }}>
          <ReplyRounded sx={{ fontSize: 17 }} />
          <Typography component="span" variant="body2" noWrap sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{b}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/* não saiu: o elo com o chip de saldo. A bolha fica "rascunho" e embaixo vai o porquê. */
function DeliveryProblem({ d }) {
  const c = d.state === 'not_sent' ? WAC.warning : WAC.danger;
  const Icon = d.state === 'not_sent' ? MoneyOffRounded : ErrorOutlineRounded;
  return (
    <Box role="note" sx={{
      mt: '4px', display: 'flex', alignItems: 'flex-start', gap: 0.75, px: 1.25, py: 0.875,
      borderRadius: '9px', bgcolor: WAC.bubbleIn, boxShadow: WAC.shadow, borderLeft: '4px solid', borderLeftColor: c,
    }}>
      <Icon aria-hidden sx={{ fontSize: 18, color: c, flexShrink: 0, mt: '1px' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: c, fontWeight: 'var(--fw-bold)' }}>{d.label}</Typography>
        <Typography variant="caption" sx={{ color: WAC.meta }}>
          {d.state === 'not_sent' ? 'Faltou crédito na hora do envio — o cliente ficou sem essa resposta.' : 'O WhatsApp não conseguiu entregar ao cliente.'}
        </Typography>
      </Box>
    </Box>
  );
}

function MessageItem({ m, first }) {
  const out = m.direction === 'out';
  const KIcon = kindIcon(m.kind);
  const problem = out && m.delivery && (m.delivery.state === 'not_sent' || m.delivery.state === 'failed');
  const problemInk = problem ? (m.delivery.state === 'not_sent' ? WAC.warning : WAC.danger) : null;
  const bg = out ? WAC.bubbleOut : WAC.bubbleIn;
  const tail = first && !problem;

  if (m.kind === 'sticker') {
    return (
      <Box component="li" sx={{ listStyle: 'none', mt: first ? 1 : '2px', display: 'flex', flexDirection: 'column', alignItems: out ? 'flex-end' : 'flex-start' }}>
        <Box sx={{
          width: 120, height: 120, borderRadius: '12px', border: '2px dashed', borderColor: 'color-mix(in srgb, var(--wa-meta, #56656F) 45%, transparent)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: WAC.meta,
        }}>
          <EmojiEmotionsOutlined sx={{ fontSize: 40 }} />
          <Typography variant="caption" sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{m.label}</Typography>
        </Box>
        <Typography variant="caption" sx={{ mt: 0.5, px: 0.75, borderRadius: '6px', bgcolor: WAC.bubbleIn, color: WAC.meta, boxShadow: WAC.shadow }}>{m.timeLabel}</Typography>
      </Box>
    );
  }

  return (
    <Box component="li" sx={{ listStyle: 'none', mt: first ? 1 : '2px', display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', px: 1 }}>
      <Box sx={{ maxWidth: '86%', minWidth: 0 }}>
        <Box sx={{
          position: 'relative', px: 1.125, pt: 0.75, pb: 0.5, minWidth: 110,
          borderRadius: '10px',
          ...(tail ? (out ? { borderTopRightRadius: 0 } : { borderTopLeftRadius: 0 }) : {}),
          bgcolor: problem ? 'color-mix(in srgb, var(--wa-bubble) 50%, transparent)' : bg,
          border: problem ? '1.5px dashed' : 0, borderColor: problemInk || 'transparent',
          boxShadow: problem ? 'none' : WAC.shadow,
          // o "rabinho" do WhatsApp na primeira bolha de cada sequência
          '&::before': tail ? {
            content: '""', position: 'absolute', top: 0, width: 8, height: 13, bgcolor: bg,
            ...(out
              ? { right: -8, clipPath: 'polygon(0 0, 100% 0, 0 100%)' }
              : { left: -8, clipPath: 'polygon(0 0, 100% 0, 100% 100%)' }),
          } : {},
        }}>
          {/* o rótulo abre a bolha: "Lembrete", "Tocou no botão", "Resposta automática"… */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.375, color: out ? WAC.ink : WAC.meta }}>
            <KIcon aria-hidden sx={{ fontSize: 14 }} />
            <Typography component="span" variant="caption" sx={{ fontWeight: 'var(--fw-bold)', color: 'inherit' }}>{m.label}</Typography>
          </Box>
          {m.replyTo && <Quote q={m.replyTo} />}
          <Box sx={{ opacity: problem ? 0.75 : 1 }}>
            <MessageBody m={m} />
          </Box>
          {m.appointment && <AppointmentChip ap={m.appointment} />}
          <MetaLine m={m} />
        </Box>
        {Array.isArray(m.buttons) && m.buttons.length > 0 && <SentButtons buttons={m.buttons} />}
        {problem && <DeliveryProblem d={m.delivery} />}
      </Box>
    </Box>
  );
}

function ThreadSkeleton({ compact = false }) {
  const rows = compact ? [0, 1] : [0, 1, 2, 3];
  return (
    <Box role="status" aria-label="Carregando mensagens" sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
      {rows.map((i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: i % 2 ? 'flex-start' : 'flex-end' }}>
          <Skeleton variant="rounded" width={i % 2 ? '58%' : '70%'} height={i % 3 === 0 ? 64 : 40}
            sx={{ bgcolor: 'color-mix(in srgb, var(--wa-bubble-in, #FFFFFF) 55%, transparent)', borderRadius: '10px' }} />
        </Box>
      ))}
    </Box>
  );
}

function Thread({ conv, a }) {
  const msgs = conv.messages || [];
  const scrollRef = useRef(null);
  const snap = useRef({ key: null, firstId: null, lastId: null, height: 0 });

  // Scroll (UI-local): abre no fim; mensagem nova (atualização de 20 s) desliza até ela; "carregar anteriores"
  // mantém o ponto de leitura no lugar em vez de pular para o topo.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const first = msgs[0]?.id ?? null;
    const last = msgs[msgs.length - 1]?.id ?? null;
    const s = snap.current;
    if (s.key !== conv.key) {
      // abre no fim — e de novo nos 2 quadros seguintes: dentro do Dialog (Fade + portal) e com fontes/emoji
      // chegando, a altura final só assenta depois do primeiro layout
      const toEnd = () => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; };
      toEnd();
      requestAnimationFrame(() => { toEnd(); requestAnimationFrame(toEnd); });
      setTimeout(toEnd, 250);
    }
    else if (first !== s.firstId && last === s.lastId) el.scrollTop += el.scrollHeight - s.height;
    else if (last !== s.lastId) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    snap.current = { key: conv.key, firstId: first, lastId: last, height: el.scrollHeight };
  });

  // agrupamento do WhatsApp: rabinho e respiro maior só na 1ª bolha de cada sequência do mesmo lado
  const items = [];
  let day = null;
  let prevDir = null;
  let markedNew = false;
  msgs.forEach((m) => {
    let first = m.direction !== prevDir;
    if (m.dayLabel !== day) { day = m.dayLabel; first = true; items.push(<DayChip key={`d-${m.id}`} label={m.dayLabel} />); }
    if (!markedNew && m.direction === 'in' && m.unread) { markedNew = true; first = true; items.push(<NewMessagesDivider key={`n-${m.id}`} />); }
    items.push(<MessageItem key={m.id} m={m} first={first} />);
    prevDir = m.direction;
  });

  return (
    <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', bgcolor: WAC.chatBg, px: { xs: 1.25, sm: 1.5 }, py: 1.5 }}>
      {conv.loadingMore && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.25 }}>
            <CircularProgress size={14} thickness={5} sx={{ color: WAC.meta }} />
            <Typography variant="caption" sx={{ color: WAC.dayFg }}>Carregando anteriores…</Typography>
          </Box>
          <ThreadSkeleton compact />
        </Box>
      )}
      {conv.hasMore && !conv.loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
          <Button size="small" onClick={() => a.onLoadMore()} startIcon={<KeyboardArrowUpRounded />}
            sx={{
              minHeight: 36, px: 1.5, borderRadius: 999, bgcolor: WAC.bubbleIn, color: WAC.ink,
              boxShadow: WAC.shadow, fontWeight: 'var(--fw-bold)',
              '&:hover': { bgcolor: WAC.bubbleIn, boxShadow: '0 2px 6px rgba(11, 20, 26, 0.18)' },
              '&:focus-visible': { outline: '2px solid', outlineColor: WAC.ink, outlineOffset: '2px' },
            }}>
            Carregar anteriores
          </Button>
        </Box>
      )}
      <Box component="ol" aria-label={`Mensagens com ${conv.title}`} sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column' }}>
        {items}
      </Box>
    </Box>
  );
}

/* ══════════════════════════════ CAIXA DE ESCREVER (@v3) ══════════════════════════════
 * O rodapé do WhatsApp: faixa cinza (--wa-footer-bg), pílula branca do campo e o botão verde redondo do avião de
 * papel. O (?) mora DENTRO da pílula, onde o WhatsApp põe o emoji — leve, sempre à mão, nunca um muro de texto.
 * Embaixo, em cinza de apoio: assinatura, prazo e custo (informação, não alarme). O contador só quando vier.
 * Botão de enviar: --wa-on-main sobre --wa-action = 5,3:1 (ícone); desabilitado vira disco cinza. */

/* regras de envio: popover ancorado no (?) no desktop; folha de baixo no celular (lá o popover some sob o polegar) */
function RulesSheet({ rules, anchorEl, open, onClose, fullScreen }) {
  if (!rules) return null;
  const body = (
    <Box sx={{ p: 2, pt: fullScreen ? 1 : 2, maxWidth: fullScreen ? 'none' : 340 }}>
      {fullScreen && <Box aria-hidden sx={{ width: 36, height: 4, borderRadius: 999, bgcolor: WAC.divider, mx: 'auto', mb: 1.5 }} />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <HelpOutlineRounded aria-hidden sx={{ fontSize: 20, color: WAC.ink }} />
        <Typography id="wa-rules-title" variant="subtitle2" sx={{ flex: 1, fontWeight: 'var(--fw-bold)', color: WAC.text }}>{rules.title}</Typography>
        <IconButton aria-label="Fechar regras de envio" onClick={onClose}
          sx={{ ...TAP, mr: -1, color: WAC.meta, '&:hover': { bgcolor: WAC.footerBg }, ...FOCUS }}>
          <CloseRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(rules.items || []).map((it) => (
          <Box component="li" key={it} sx={{ listStyle: 'none', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <CheckRounded aria-hidden sx={{ fontSize: 16, color: WAC.ink, mt: '2px', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: WAC.text, textWrap: 'pretty' }}>{it}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
  if (fullScreen) {
    return (
      <Drawer anchor="bottom" open={open} onClose={onClose}
        PaperProps={{ 'aria-labelledby': 'wa-rules-title', sx: {
          bgcolor: WAC.bubbleIn, backgroundImage: 'none', borderTopLeftRadius: 'var(--radius-lg)', borderTopRightRadius: 'var(--radius-lg)',
          pb: 'env(safe-area-inset-bottom)',
        } }}>
        {body}
      </Drawer>
    );
  }
  return (
    <Popover open={open} anchorEl={anchorEl} onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { 'aria-labelledby': 'wa-rules-title', sx: {
        mt: -1, bgcolor: WAC.bubbleIn, backgroundImage: 'none', borderRadius: '12px',
        boxShadow: '0 8px 28px rgba(11, 20, 26, 0.22)', border: '1px solid', borderColor: WAC.divider,
      } } }}>
      {body}
    </Popover>
  );
}

function RulesButton({ onOpen, sx }) {
  return (
    <Tooltip title="Regras de envio">
      <IconButton aria-label="Ver regras de envio" aria-haspopup="dialog" onClick={onOpen}
        sx={{ ...TAP, flexShrink: 0, color: WAC.meta, '&:hover': { color: WAC.ink, bgcolor: WAC.footerBg }, ...FOCUS, ...sx }}>
        <HelpOutlineRounded sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>
  );
}

/* bloqueio = cartão de SISTEMA no próprio rodapé: é regra do WhatsApp (ou saldo), não erro do dono */
function BlockedCard({ blocked, conv, a, onRules }) {
  const Icon = blocked.action === 'buyCredits' ? PaidOutlined : blocked.action === 'replyOnWhatsApp' ? ScheduleRounded : InfoOutlined;
  const act = blocked.action === 'replyOnWhatsApp' && conv.replyHref
    ? { onClick: () => a.onReplyOnWhatsApp(conv.replyHref), start: <WhatsApp />, end: <NorthEastRounded sx={{ fontSize: '16px !important' }} /> }
    : blocked.action === 'buyCredits'
      ? { onClick: () => a.onBuyCredits(), start: <ShoppingCartOutlined />, end: null }
      : null;
  return (
    <Box role="status" sx={{ bgcolor: WAC.bubbleIn, borderRadius: '12px', boxShadow: WAC.shadow, pl: 1.5, pr: 0.5, pt: 1, pb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <Box aria-hidden sx={{ width: 36, height: 36, mt: 0.25, borderRadius: '50%', bgcolor: WAC.dayBg, color: WAC.dayFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'var(--fw-bold)', color: WAC.text }}>{blocked.title}</Typography>
          <Typography variant="body2" sx={{ color: WAC.meta, mt: 0.25, textWrap: 'pretty' }}>{blocked.text}</Typography>
        </Box>
        <RulesButton onOpen={onRules} />
      </Box>
      {act && blocked.actionLabel && (
        <Box sx={{ mt: 1.25, pl: 'calc(36px + 10px)', pr: 1 }}>
          <Button onClick={act.onClick} startIcon={act.start} endIcon={act.end}
            sx={{
              minHeight: { xs: 44, sm: 40 }, px: 2, borderRadius: 999, bgcolor: WAC.header, color: WAC.onHeader,
              fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap',
              '&:hover': { bgcolor: 'color-mix(in srgb, var(--wa-header) 86%, black)' }, ...FOCUS,
            }}>
            {blocked.actionLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function Composer({ c, conv, a, fullScreen }) {
  const [rulesAnchor, setRulesAnchor] = useState(null);   // UI-local: regras abertas
  const [rulesOpen, setRulesOpen] = useState(false);
  const helpRef = useRef(null);
  if (!c) return null;   // composer null = ainda carregando → sem caixa
  const openRules = (e) => { setRulesAnchor(e?.currentTarget || helpRef.current); setRulesOpen(true); };
  const closeRules = () => setRulesOpen(false);
  const hasText = Boolean(c.text && c.text.trim());
  const canPress = c.canSend && hasText && !c.sending;
  const send = () => { if (canPress) a.onSendMessage(); };

  const meta = (Icon, children, key) => (
    <Box key={key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Icon aria-hidden sx={{ fontSize: 14, color: WAC.meta, flexShrink: 0 }} />
      <Typography component="span" variant="caption" sx={{ color: WAC.meta }}>{children}</Typography>
    </Box>
  );

  return (
    <Box sx={{
      flexShrink: 0, bgcolor: WAC.footerBg, px: 1, pt: 1,
      pb: { xs: 'max(8px, env(safe-area-inset-bottom))', sm: 1 },
    }}>
      {!c.canSend && c.blocked ? (
        <BlockedCard blocked={c.blocked} conv={conv} a={a} onRules={openRules} />
      ) : (
        <>
          {c.errorMessage && (
            <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, pb: 0.75 }}>
              <ErrorOutlineRounded aria-hidden sx={{ fontSize: 16, color: WAC.danger, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: WAC.danger, fontWeight: 'var(--fw-bold)' }}>{c.errorMessage}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            {/* a pílula branca do WhatsApp; o (?) no lugar do emoji */}
            <Box sx={{
              flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', bgcolor: WAC.bubbleIn,
              borderRadius: '24px', boxShadow: WAC.shadow, pl: 0, pr: 1.5, boxSizing: 'border-box',
              minHeight: { xs: 48, sm: 44 },   // 44px de alvo (xs) / 40px (sm) + 2px de borda de cada lado
              border: '2px solid', borderColor: c.errorMessage ? WAC.danger : 'transparent',
              '&:focus-within': { borderColor: c.errorMessage ? WAC.danger : WAC.ink },
            }}>
              <Box ref={helpRef} sx={{ display: 'flex', alignSelf: 'flex-end' }}>
                <RulesButton onOpen={openRules} />
              </Box>
              <InputBase
                multiline maxRows={5} fullWidth
                value={c.text || ''}
                placeholder={c.placeholder}
                readOnly={c.sending}
                onChange={(e) => a.onComposerChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
                }}
                inputProps={{ maxLength: c.maxLength, 'aria-label': `Mensagem para ${conv.title}`, 'aria-describedby': 'wa-composer-meta' }}
                sx={{
                  flex: 1, py: { xs: '11px', sm: '9px' }, color: WAC.text, fontFamily: 'inherit',
                  '& textarea': { p: 0, overflowWrap: 'anywhere' },
                  '& textarea::placeholder': { color: WAC.meta, opacity: 1 },
                }}
              />
            </Box>
            <Tooltip title={c.sending ? 'Enviando…' : 'Enviar (Enter)'}>
              <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                <IconButton aria-label={c.sending ? 'Enviando mensagem' : 'Enviar mensagem'} aria-busy={c.sending || undefined}
                  onClick={send} disabled={!canPress}
                  sx={{
                    width: { xs: 48, sm: 44 }, height: { xs: 48, sm: 44 },   // mesma altura da pílula
                    bgcolor: 'var(--wa-action)', color: 'var(--wa-on-main)', boxShadow: WAC.shadow,
                    '&:hover': { bgcolor: 'color-mix(in srgb, var(--wa-action) 88%, black)' },
                    '&.Mui-disabled': {
                      bgcolor: c.sending ? 'var(--wa-action)' : WAC.avatarBg,
                      color: c.sending ? 'var(--wa-on-main)' : WAC.meta,
                    },
                    '&:focus-visible': { outline: '2px solid', outlineColor: WAC.ink, outlineOffset: '2px' },
                  }}>
                  {c.sending
                    ? <CircularProgress size={20} thickness={5} aria-hidden sx={{ color: 'var(--wa-on-main)' }} />
                    : <SendRounded sx={{ fontSize: 21, ml: '2px' }} />}
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
          {/* apoio, em cinza: assinatura · prazo · custo — e o contador só perto do limite */}
          <Box id="wa-composer-meta" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 0.25, px: 1.25, pt: 0.75 }}>
            {c.signatureLabel && meta(EditOutlined, <WaText text={c.signatureLabel} />, 'sig')}
            {c.windowLabel && meta(ScheduleRounded, c.windowLabel, 'win')}
            {c.costLabel && meta(PaidOutlined, c.costLabel, 'cost')}
            {c.counterLabel && (
              <Typography component="span" variant="caption" aria-live="polite"
                sx={{ ml: 'auto', color: WAC.warning, fontWeight: 'var(--fw-bold)', fontVariantNumeric: 'tabular-nums' }}>
                {c.counterLabel}
              </Typography>
            )}
          </Box>
        </>
      )}
      <RulesSheet rules={c.rules} anchorEl={rulesAnchor || helpRef.current} open={rulesOpen} onClose={closeRules} fullScreen={fullScreen} />
    </Box>
  );
}

/* ══════════════════════════════════ PAINEL ══════════════════════════════════ */

export default function WhatsAppInboxView({ vm, actions }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // actions com no-op default: a View nunca quebra por callback ausente
  const a = useMemo(() => new Proxy(actions || {}, { get: (t, k) => t[k] || (() => {}) }), [actions]);
  if (!vm) return null;

  const state = vm.state;
  const conv = state === 'conversation' ? vm.conversation : null;
  const onClose = () => a.onClose();

  let body;
  if (conv) {
    body = (
      <>
        <ConversationHeader conv={conv} a={a} />
        <Thread conv={conv} a={a} />
        <Composer key={conv.key} c={conv.composer} conv={conv} a={a} fullScreen={fullScreen} />
      </>
    );
  } else {
    let content;
    if (state === 'loading') {
      content = (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', bgcolor: WAC.bubbleIn }}>
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: WAC.divider }}><Skeleton variant="rounded" height={40} sx={{ bgcolor: WAC.footerBg, borderRadius: '10px' }} /></Box>
          <Box role="status" aria-label="Carregando conversas">{[0, 1, 2, 3, 4].map((i) => <RowSkeleton key={i} i={i} />)}</Box>
        </Box>
      );
    } else if (state === 'error') {
      content = (
        <StateBlock
          icon={<CloudOffRounded sx={{ fontSize: 30, color: WAC.danger }} />} iconBg="color-mix(in srgb, var(--wa-danger, #B42318) 10%, var(--wa-bubble-in, #FFFFFF))"
          title="Não deu para abrir as conversas"
          description={vm.errorMessage}
          action={(
            <Button onClick={() => a.onRetry()} startIcon={<ReplayRounded />}
              sx={{ minHeight: 40, px: 2, borderRadius: 999, fontWeight: 'var(--fw-bold)', color: WAC.onHeader, bgcolor: WAC.header, '&:hover': { bgcolor: 'color-mix(in srgb, var(--wa-header) 86%, black)' }, ...FOCUS }}>
              Tentar novamente
            </Button>
          )}
        />
      );
    } else if (state === 'empty') {
      content = (
        <StateBlock
          title="Nenhuma conversa ainda"
          description="Quando um cliente responder, tocar em Confirmar ou Cancelar, ou escrever para o número do Agendário, a conversa aparece aqui."
        >
          <EmptyGhostThread />
        </StateBlock>
      );
    } else if (state === 'noResults') {
      content = (
        <>
          <SearchField value={vm.searchQuery} onSearch={(q) => a.onSearch(q)} />
          <StateBlock
            icon={<SearchOffRounded sx={{ fontSize: 30, color: WAC.meta }} />}
            title={`Nada encontrado para “${vm.searchQuery}”`}
            description="Busque pelo nome do cliente ou por parte do telefone."
            action={(
              <Button onClick={() => a.onSearch('')} startIcon={<ClearRounded />}
                sx={{ minHeight: 40, borderRadius: 999, px: 2, bgcolor: WAC.footerBg, color: WAC.ink, fontWeight: 'var(--fw-bold)', '&:hover': { bgcolor: WAC.divider }, ...FOCUS }}>
                Limpar busca
              </Button>
            )}
          />
        </>
      );
    } else {
      content = (
        <>
          <SearchField value={vm.searchQuery} onSearch={(q) => a.onSearch(q)} />
          <ConversationList vm={vm} a={a} />
        </>
      );
    }
    body = (
      <>
        <ListHeader onClose={onClose} />
        {content}
        <ListFooter onSeeAll={() => a.onSeeAll()} />
      </>
    );
  }

  return (
    <Dialog
      open={!!vm.open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby="wa-inbox-title"
      // convive com outros flutuantes do Admin (widget de suporte, seletores) sem roubar o foco deles
      disableEnforceFocus
      slotProps={{
        backdrop: { sx: { bgcolor: fullScreen ? 'transparent' : 'color-mix(in srgb, var(--text-primary) 10%, transparent)' } },
      }}
      PaperProps={{
        elevation: 0,
        sx: fullScreen ? {
          height: '100dvh', maxHeight: '100dvh',
          bgcolor: WAC.bubbleIn, color: WAC.text, backgroundImage: 'none',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        } : {
          // ancorado no topo, à direita, logo abaixo da barra (64px + respiro)
          position: 'fixed', top: 72, right: { sm: 12, md: 16 }, m: 0,
          width: 400, maxWidth: 'calc(100vw - 24px)', height: 'min(720px, 80vh)', maxHeight: '80vh',
          bgcolor: WAC.bubbleIn, color: WAC.text, backgroundImage: 'none',
          border: '1px solid var(--divider)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-hover)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        },
      }}
    >
      {body}
    </Dialog>
  );
}
